import { cn } from '../lib/utils';
import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/Button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Navigate, useNavigate } from "react-router-dom";
import { StatCard } from '../components/common/StatCard';
import { Chart } from '../components/features/dashboard/Chart';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { useMicrosoftAuth } from "../context/MicrosoftAuthContext";
import { useTheme } from "../context/ThemeContext";
import { toast } from 'react-toastify';
import { getCountdown, getPeriodLabel } from '../utils/formatters';
import { cancelSubmission, exportEmailStatistics, getDashboardStats } from '../api/submissionService';
import { downloadBlobFile } from '../utils/fileHelper';

const INIT_STATS = [
    { totalBranches: "0", id: '', AnalyticsIcon: "bluecut", details: "Total Submissions", CardColor: "text-black border rounded-20", buttonLabel: "", btnClassName: '' },
    { totalBranches: "0", id: '', AnalyticsIcon: "success", details: "Successful Submissions", CardColor: "text-black border rounded-20", buttonLabel: "", btnClassName: '' },
    { totalBranches: "0", id: '', AnalyticsIcon: "dangur", details: "Cancelled Submissions", CardColor: "text-black border rounded-20", buttonLabel: "", btnClassName: '' },
    { totalBranches: "00:00:00", id: '', AnalyticsIcon: "analytics", details: "Time to cancel on latest submission", CardColor: "text-black border rounded-20", buttonLabel: "Cancel", btnClassName: 'text-FF0000 bg-FFCBCB' },
]

const Dashboard = () => {
    const { isAuthenticated, user, userTeamData, accessToken, jwtToken } = useMicrosoftAuth();
    const { isDark } = useTheme();
    const navigate = useNavigate();

    const [isExportingEmailStats, setIsExportingEmailStats] = useState(false);
    const isAdmin = userTeamData?.user_role_id?.includes('10') || userTeamData?.user_role_id?.includes('11');

    const [stats, setStats] = useState(INIT_STATS);

    const [timeRange, setTimeRange] = useState('This Week');
    const [periodOffset, setPeriodOffset] = useState(0);
    const [error, setError] = useState(null);

    // Update API headers when auth tokens change
    useEffect(() => {
        setStats(prev => prev.map(item =>
            item.details === 'Total Submissions' || item.details === 'My Submissions'
                ? { ...item, details: isAdmin ? 'Total Submissions' : 'My Submissions' }
                : item
        ));
    }, [isAdmin]);


    const handleTimeRangeChange = (range) => {
        setTimeRange(range);
        setPeriodOffset(0);
        fetchStats(range, 0);
    };

    const handleOffsetChange = (delta) => {
        const newOffset = periodOffset + delta;
        if (newOffset > 0) return;
        setPeriodOffset(newOffset);
        fetchStats(timeRange, newOffset);
    };

    const handleCancelSubmission = async (id) => {
        if (!id) {
            console.log('No submission ID provided for cancellation');
            return;
        }

        try {
            const message = await cancelSubmission(id, userTeamData);
            toast.success(message);
            fetchStats();
        } catch (error) {
            toast.error(error.message);
        }
    };


    const fetchStats = async (rangeFilter = null, offsetFilter = null) => {
        setError(null);

        try {
            const range = rangeFilter || timeRange;
            const offset = offsetFilter !== null ? offsetFilter : periodOffset;

            const newStats = await getDashboardStats(range, offset, userTeamData);
            if (!newStats) return;

            setStats(prevData =>
                prevData.map(item => {
                    switch (item.details) {
                        case 'Total Submissions':
                        case 'My Submissions':
                            return { ...item, totalBranches: newStats.totalSubmissions.toString() };
                        case 'Successful Submissions':
                            return { ...item, totalBranches: newStats.successfulSubmissions.toString() };
                        case 'Cancelled Submissions':
                            return { ...item, totalBranches: newStats.cancelledSubmissions.toString() };
                        case 'Time to cancel on latest submission':
                            return {
                                ...item,
                                totalBranches: newStats.timeToCancel || '--:--',
                                id: newStats.latestSubmissionId || ''
                            };
                        default:
                            return item;
                    }
                })
            );
        } catch (err) {
            setError(err.message);
            toast.error(err.message);
        }
    };

    const handleExportEmailStatistics = async () => {
        if (!isAdmin) {
            toast.error('Access denied. Admin role required.');
            return;
        }

        setIsExportingEmailStats(true);
        try {

            const result = await exportEmailStatistics(userTeamData);
            const filename = `monthly_email_statistics_${new Date().toISOString().split('T')[0]}.csv`;
            downloadBlobFile(result.data, filename);
            if (result.message) toast.success(result.message);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setIsExportingEmailStats(false);
        }
    };

    useEffect(() => {
        fetchStats();
        // Set up interval for countdown timer
        const timer = setInterval(() => {
            setStats(prevData => prevData.map(item => {
                if (item.details === 'Time to cancel on latest submission' && item.totalBranches !== '--:--') {
                    const timestr = getCountdown(item.totalBranches);
                    if (timestr === '00:00:00') {
                        // If countdown reaches zero, refresh stats
                        fetchStats();
                        return { ...item, totalBranches: '--:--', id: '' };
                    }
                    return { ...item, totalBranches: timestr };
                }
                return item;
            }));
        }, 1000);

        // Set up interval to refresh stats periodically
        const statsRefresh = setInterval(() => {
            fetchStats();
        }, 30000); // Refresh every 30 seconds

        return () => {
            clearInterval(timer);
            clearInterval(statsRefresh);
        };
    }, [jwtToken, accessToken]);

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return (
        <div
            className='min-h-screen px-[16px] sm:px-[24px] py-[20px] sm:py-[30px] flex flex-col gap-[20px] sm:gap-[30px] transition-colors duration-200'
            style={{ backgroundColor: isDark ? '#000000' : '#F5F5F5' }}
        >
            {error && (
                <div className={cn("bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded")} role="alert">
                    {error}
                </div>
            )}
            {/* Welcome Section */}
            <div className='flex flex-col sm:flex-row sm:items-center gap-[16px] sm:gap-[30px] items-start'>
                <div className='flex-1 flex flex-col gap-[6px]'>
                    <h1
                        className='text-[18px] sm:text-[20px] font-bold m-0 transition-colors duration-200'
                        style={{ color: isDark ? '#ffffff' : '#072837' }}
                    >
                        Hi, {user ? user.name : 'Guest'}
                    </h1>
                    <p
                        className={cn("text-[14px] sm:text-[16px] font-normal m-0 transition-colors duration-200")}
                        style={{ color: isDark ? '#b0b0b0' : '#686868' }}
                    >
                        Welcome to the CNG Mailroom Dashboard. Here you can track your submissions and analytics.
                    </p>
                </div>
                <Button
                    onClick={() => navigate('/Submissionprogress')}
                    variant="ghost"
                    className={cn(
                        "px-[16px] py-[12px] rounded-[12px] text-[14px] sm:text-[16px] font-semibold whitespace-nowrap transition-colors",
                        isDark
                            ? 'bg-[#cd6d2c] text-[#000000] hover:bg-[#ffffff]'
                            : 'bg-[#cd6d2c] text-white hover:bg-[#072837]'
                    )}
                >
                    + New Submission
                </Button>
            </div>
            {/* Stats Cards Section */}
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[12px] sm:gap-[16px]'>
                {stats.map((item, index) => (
                    <StatCard
                        key={index}
                        details={item.details}
                        totalBranches={item.totalBranches}
                        AnalyticsIcon={item.AnalyticsIcon}
                        buttonLabel={item.buttonLabel}
                        btnClassName={item.btnClassName}
                        id={item.id}
                        handleCancelSubmission={handleCancelSubmission}
                        isDark={isDark}
                    />
                ))}
            </div>

            {/* Monthly Email Statistics Section - Admin Only */}
            {isAdmin && (
                <div
                    className='border-solid border-[1px] rounded-[16px] sm:rounded-[20px] p-[16px] sm:p-[24px] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-[12px] transition-colors duration-200'
                    style={{
                        backgroundColor: isDark ? '#161515' : '#ffffff',
                        borderColor: isDark ? '#292929' : '#d9d9d9'
                    }}
                >
                    <div className='flex flex-col gap-[4px]'>
                        <h3
                            className='text-[16px] sm:text-[18px] font-semibold m-0 transition-colors duration-200'
                            style={{ color: isDark ? '#ffffff' : '#072837' }}
                        >
                            Monthly Email Statistics
                        </h3>
                        <p
                            className='text-[13px] sm:text-[14px] font-normal m-0 transition-colors duration-200'
                            style={{ color: isDark ? '#b0b0b0' : '#686868' }}
                        >
                            Click on "Download" to export email statistics.
                        </p>
                    </div>
                    <Button
                        onClick={handleExportEmailStatistics}
                        disabled={isExportingEmailStats}
                        className={cn(
                            "flex items-center gap-[6px] whitespace-nowrap font-semibold text-[16px] border-0 transition-colors duration-200",
                            isDark
                                ? 'bg-white text-[#000000] hover:!bg-[#cd6d2c] hover:text-white'
                                : 'bg-[#072837] text-white hover:!bg-[#cd6d2c]',
                            isExportingEmailStats ? 'opacity-70 cursor-not-allowed' : ''
                        )}
                    >
                        <svg className="fill-current" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14">
                            <path d="M13.435 3.9L12.5 4.69V3.295L13.285 3.795C13.3368 3.82736 13.3869 3.86241 13.435 3.9ZM13.96 4.765L12.5 6L7.965 9.84C7.79427 9.97869 7.59685 10.0808 7.385 10.14C7.30093 10.1598 7.21579 10.1749 7.13 10.185H6.87C6.78421 10.1749 6.69907 10.1598 6.615 10.14C6.405 10.0825 6.20922 9.98202 6.04 9.845L1.5 6L0.04 4.765C0.0165458 4.86169 0.00314142 4.96055 0 5.06V12.5C0 12.8978 0.158035 13.2794 0.43934 13.5607C0.720644 13.842 1.10218 14 1.5 14H12.5C12.8978 14 13.2794 13.842 13.5607 13.5607C13.842 13.2794 14 12.8978 14 12.5V5.06C13.9969 4.96055 13.9835 4.86169 13.96 4.765ZM6.68 9.075L2.5 5.535V1.5C2.5 1.10218 2.65804 0.720644 2.93934 0.43934C3.22064 0.158035 3.60218 0 4 0H10C10.3978 0 10.7794 0.158035 11.0607 0.43934C11.342 0.720644 11.5 1.10218 11.5 1.5V5.535L7.32 9.075C7.23016 9.14983 7.11693 9.19081 7 9.19081C6.88307 9.19081 6.76984 9.14983 6.68 9.075ZM4.5 3C4.5 3.13261 4.55268 3.25979 4.64645 3.35355C4.74021 3.44732 4.86739 3.5 5 3.5H9C9.13261 3.5 9.25979 3.44732 9.35355 3.35355C9.44732 3.25979 9.5 3.13261 9.5 3C9.5 2.86739 9.44732 2.74021 9.35355 2.64645C9.25979 2.55268 9.13261 2.5 9 2.5H5C4.86739 2.5 4.74021 2.55268 4.64645 2.64645C4.55268 2.74021 4.5 2.86739 4.5 3ZM5 5.5H9C9.13261 5.5 9.25979 5.44732 9.35355 5.35355C9.44732 5.25979 9.5 5.13261 9.5 5C9.5 4.86739 9.44732 4.74021 9.35355 4.64645C9.25979 4.55268 9.13261 4.5 9 4.5H5C4.86739 4.5 4.74021 4.55268 4.64645 4.64645C4.55268 4.74021 4.5 4.86739 4.5 5C4.5 5.13261 4.55268 5.25979 4.64645 5.35355C4.74021 5.44732 4.86739 5.5 5 5.5ZM1.5 3.295L0.71 3.795C0.659462 3.83226 0.611053 3.87232 0.565 3.915L1.5 4.69V3.295Z" />
                        </svg>
                        {isExportingEmailStats ? 'Exporting...' : 'Export Email Statistics'}
                    </Button>
                </div>
            )}

            {/* Submission Analytics Section */}
            <div className='flex flex-col gap-[12px] sm:gap-[16px]'>
                <h2
                    className='text-[18px] sm:text-[20px] font-semibold transition-colors duration-200'
                    style={{ color: isDark ? '#ffffff' : '#072837' }}
                >
                    Submission Analytics
                </h2>

                <div
                    className='border-solid border-[1px] rounded-[16px] sm:rounded-[20px] p-[16px] sm:p-[24px] flex flex-col gap-[20px] sm:gap-[24px] transition-colors duration-200'
                    style={{
                        backgroundColor: isDark ? '#161515' : '#ffffff',
                        borderColor: isDark ? '#292929' : '#d9d9d9'
                    }}
                >
                    {/* Header with Time Range Selector */}
                    <div className='flex flex-col sm:flex-row sm:items-center gap-[12px] sm:gap-0 sm:justify-between'>
                        <div className='flex items-center gap-[8px]'>
                            <div
                                className='rounded-[8px] w-[8px] h-[8px] transition-colors duration-200'
                                style={{ backgroundColor: isDark ? '#cd6d2c' : '#072837' }}
                            />
                            <span
                                className='text-[13px] sm:text-[14px] font-normal transition-colors duration-200'
                                style={{ color: isDark ? '#686868' : '#686868' }}
                            >
                                {isAdmin ? 'Total Submissions' : 'My Submissions'}
                            </span>
                        </div>

                        <div className='self-end sm:self-auto flex items-center gap-[6px]'>
                            <button
                                type="button"
                                onClick={() => handleOffsetChange(-1)}
                                className={cn("border-solid border-[1px] flex items-center justify-center h-[36px] sm:h-[40px] w-[36px] sm:w-[40px] rounded-[12px] cursor-pointer transition-colors duration-200")}
                                style={{
                                    backgroundColor: isDark ? '#161515' : '#ffffff',
                                    borderColor: isDark ? '#292929' : '#d9d9d9',
                                    color: isDark ? '#cd6d2c' : '#072837'
                                }}
                            >
                                <ChevronLeft className={cn("w-[16px] h-[16px]")} />
                            </button>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button
                                        type="button"
                                        className={cn("border-solid border-[1px] flex items-center gap-[10px] h-[36px] sm:h-[40px] px-[12px] sm:px-[16px] py-[6px] rounded-[12px] text-[14px] sm:text-[16px] font-normal cursor-pointer transition-colors duration-200")}
                                        style={{
                                            backgroundColor: isDark ? '#161515' : '#ffffff',
                                            borderColor: isDark ? '#292929' : '#d9d9d9',
                                            color: isDark ? '#cd6d2c' : '#072837'
                                        }}
                                    >
                                        <div
                                            className={cn("w-[14px] h-[14px] transition-colors duration-200")}
                                            style={{
                                                backgroundColor: isDark ? '#cd6d2c' : '#072837',
                                                WebkitMaskImage: "url('/assets/icons/calendar.svg')",
                                                WebkitMaskRepeat: 'no-repeat',
                                                WebkitMaskSize: 'contain',
                                                WebkitMaskPosition: 'center',
                                                maskImage: "url('/assets/icons/calendar.svg')",
                                                maskRepeat: 'no-repeat',
                                                maskSize: 'contain',
                                                maskPosition: 'center'
                                            }}
                                            aria-hidden="true"
                                        />
                                        {getPeriodLabel(timeRange, periodOffset)}
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className={cn(
                                    isDark ? 'bg-[#161515] border-[#292929]' : 'bg-white border-[#d9d9d9]',
                                    "border border-solid border-[1px] rounded-[12px] p-0 overflow-hidden"
                                )}
                                >
                                    {['This Week', 'This Month', 'This Year'].map((label) => (
                                        <DropdownMenuItem
                                            key={label}
                                            className={cn(isDark
                                                ? timeRange === label
                                                    ? 'bg-[#cd6d2c] text-[#161515] hover:!bg-[#cd6d2c]'
                                                    : 'bg-[#161515] text-[#f2f2f2] hover:bg-[#222222] hover:text-[#cd6d2c]'
                                                : timeRange === label
                                                    ? 'bg-[#072837] text-white hover:!bg-[#072837]'
                                                    : 'bg-white text-[#072837] hover:!bg-[#f5f5f5]', "text-[13px] font-medium px-[16px] py-[10px] transition-colors duration-200")}
                                            onClick={() => handleTimeRangeChange(label)}
                                        >
                                            {label}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                            <button
                                type="button"
                                onClick={() => handleOffsetChange(1)}
                                disabled={periodOffset === 0}
                                className={cn("border-solid border-[1px] flex items-center justify-center h-[36px] sm:h-[40px] w-[36px] sm:w-[40px] rounded-[12px] transition-colors duration-200")}
                                style={{
                                    backgroundColor: isDark ? '#161515' : '#ffffff',
                                    borderColor: isDark ? '#292929' : '#d9d9d9',
                                    color: periodOffset === 0 ? (isDark ? '#3a3a3a' : '#d9d9d9') : (isDark ? '#cd6d2c' : '#072837'),
                                    cursor: periodOffset === 0 ? 'default' : 'pointer'
                                }}
                            >
                                <ChevronRight className={cn("w-[16px] h-[16px]")} />
                            </button>
                        </div>
                    </div>

                    {/* Chart */}
                    <div className="w-full">
                        <Chart timeRange={timeRange} periodOffset={periodOffset} isDark={isDark} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
